// The client-side contract of the brand's galaxy gift drops ("Ajándékláda a
// galaxisban", the Challenges / Játékok page). Everything the page assumes
// about the API lives here, so it can be re-aligned with the API's final
// contract in one place. Mirrors admin/src/features/challenges/giftDropShape.js,
// narrowed to one brand.
//
// Contract (outfino-api style-universe branch: routes/brand_gift_drops_routes.js,
// services/gift_drops/wire.js brandDrop, middlewares/socket/gift_drops_socket_handler.js):
//   Auth like the campaigns: requireAuthOrShopify + requireBrandPermission('manageCampaigns') —
//   the raw partner token, or `Bearer <App Bridge session token>` embedded (config/api.js).
//   POST /v3/brands/:brandId/gift-drops              { note? ≤ NOTE_MAX, followersOnly?: boolean, rewardType: null }
//        followersOnly: only the brand's followers may catch it — everyone else sees it chained shut
//        → 201 { drop, serverNowMs }
//        409 gift_drop_max_active { maxActive, environment } — the galaxy as a whole is full (global
//        cap). A brand has NO limit of its own (CEO decision, 2026-10-14): any number of its chests
//        may be active at once, any number a day.
//        403 gift_drop_brand_inactive, 403 gift_drop_not_eligible, 400 gift_drop_invalid { field, reason }
//   GET  /v3/brands/:brandId/gift-drops?status=&limit=1..100&before=(ISO|ms)
//        → { serverNowMs, brandId, environment, maxActive, activeCount, nextBefore, drops }
//        the brand's own drops, newest first; maxActive / activeCount are the environment's GLOBAL values
//   POST /v3/brands/:brandId/gift-drops/:dropId/cancel → { drop, serverNowMs }
//        404 gift_drop_not_found, 409 gift_drop_not_active { status }
//   Envelope: success { code, message: <payload>, details } — api() unwraps `message`;
//             error   { code, message: '<error_code>', details }; the permission middleware's own
//             { success: false, message: <sentence>, error: 'FORBIDDEN' | 'INSUFFICIENT_PERMISSIONS' |
//             'BRAND_NOT_FOUND' }. api() throws with err.status and err.data = the body.
//   Drop (brand shape): id, status, environment, source { kind: 'brand', brandId, name, logoUrl },
//     pathVersion, seed, path, lifetimeMs, createdAtMs, startsAtMs, claimOpensAtMs (enters the rim),
//     claimClosesAtMs (leaves the rim on the far side), expiresAtMs (gone), collectedAtMs,
//     expiredAtMs, cancelledAtMs, cancelledByKind, ISO copies (createdAt, startsAt, expiresAt,
//     collectedAt, expiredAt, cancelledAt), winner { display: 'user', userId, name, username,
//     avatarUrl } | { display: 'anonymous' } | null, reactionMs, note, followersOnly.
//   Socket (portal only — the socket refuses a Shopify session token, so embedded polls):
//     io('<api origin>/style-universe', { auth: { token, giftOnly: true, brandId } }) → the
//     `gift:brand:<brandId>` room. Events GIFT_EVENTS: gift_drops_state { protocol, brandId,
//     serverNowMs, drops } (the live drops; REPLACES the list), gift_drop_spawned / _collected /
//     _won / _expired / _cancelled { serverNowMs, brandId, dropId, source, drop?, winner?, *AtMs }.
//     Not allowed → gift_error { code: 'gift_drop_brand_forbidden' }, then a disconnect.
//   While the socket is down the page polls: POLL_ACTIVE_MS with an active chest, POLL_IDLE_MS without.
//
// v2.1 / v2.2 (wheel3d/WHEEL_CONTRACT.md "Gift drop prizes"): the brand may attach PRODUCTS —
//   POST … { …, productIds?: uuid[] (0..PRODUCTS_MAX, the brand's own ACTIVE (dev: UNLISTED too), in-stock
//   products, qty 1), freeShippingAccepted: true (required when productIds is non-empty) };
//   every drop gains prize: { products: PrizeProduct[], freeShipping: true } | null. Whoever catches the
//   chest gets them free in the cart with mandatory free shipping (the brand pays it). Product refusals:
//   400 gift_drop_invalid { field: 'productIds' | 'freeShippingAccepted', reason, productId? } (assumed to mirror
//   the wheel's reasons). `rewardType` stays null on the wire (the server stores the reward itself).

import { normalizeProduct } from './wheel/wheelProducts';

export const PATHS = {
  list: (brandId) => `/brands/${encodeURIComponent(brandId)}/gift-drops`,
  create: (brandId) => `/brands/${encodeURIComponent(brandId)}/gift-drops`,
  cancel: (brandId, id) =>
    `/brands/${encodeURIComponent(brandId)}/gift-drops/${encodeURIComponent(id)}/cancel`,
};

export const SOCKET_NAMESPACE = '/style-universe';
export const GIFT_EVENTS = [
  'gift_drops_state',
  'gift_drop_spawned',
  'gift_drop_collected',
  'gift_drop_won',
  'gift_drop_expired',
  'gift_drop_cancelled',
];

export const NOTE_MAX = 280;
/** Products one chest can hold (contract v2.2). */
export const PRODUCTS_MAX = 5;
export const HISTORY_PAGE_SIZE = 20;
export const ACTIVE_LIMIT = 50;

/** Polling cadence (no brand socket room yet). */
export const POLL_ACTIVE_MS = 5000;
export const POLL_IDLE_MS = 30000;
/** The server closes a drop at expiresAt + a short grace; read again a bit after that. */
export const EXPIRY_RECHECK_MS = 2000;

export const STATUSES = ['active', 'collected', 'expired', 'cancelled'];

/** ISO string, epoch ms, or a numeric string → epoch ms; anything else → null. */
export function toMs(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value.getTime() : null;
  if (typeof value === 'string' && /^\d+$/.test(value)) return Number(value);
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

/** First of `${key}Ms` / `key` / snake_case that parses as a time. */
function timeField(raw, key) {
  const snake = key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
  for (const k of [`${key}Ms`, key, `${snake}_ms`, snake]) {
    const ms = toMs(raw?.[k]);
    if (ms !== null) return ms;
  }
  return null;
}

const finiteOrNull = (v) => (v !== null && v !== undefined && Number.isFinite(Number(v)) ? Number(v) : null);

/**
 * @typedef {object} Winner
 * @property {string} name        display name or username ('' when hidden)
 * @property {string|null} username
 * @property {string|null} avatarUrl
 * @property {boolean} anonymous  the API hid who it is (private profile / junior)
 */

/** @returns {Winner|null} */
export function normalizeWinner(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const anonymous = Boolean(raw.anonymous ?? raw.isAnonymous ?? raw.display === 'anonymous');
  const username = raw.username ?? null;
  const name = anonymous ? '' : String(raw.displayName ?? raw.display_name ?? raw.name ?? username ?? '');
  return {
    name,
    username: anonymous ? null : username,
    avatarUrl: anonymous ? null : (raw.avatarUrl ?? raw.avatar_url ?? null),
    anonymous,
  };
}

/**
 * @typedef {object} GiftDrop
 * @property {string} id
 * @property {string} status         active | collected | expired | cancelled (or whatever the API sent)
 * @property {string|null} environment
 * @property {number|null} createdAt  epoch ms (server clock)
 * @property {number|null} startsAt   at p0, just outside the rim
 * @property {number|null} claimOpensAt  enters the rim
 * @property {number|null} claimClosesAt leaves the rim on the far side
 * @property {number|null} expiresAt  gone
 * @property {number|null} collectedAt
 * @property {number|null} expiredAt
 * @property {number|null} cancelledAt
 * @property {number|null} crossingMs rim to rim (expiresAt − startsAt), the "crossing"
 * @property {Winner|null} winner
 * @property {number|null} reactionMs tap − entering the rim
 * @property {string} note
 * @property {boolean} followersOnly only the brand's followers can catch it
 * @property {{ kind: string, brandId: string|null, name: string|null, logoUrl: string|null }|null} source
 */

/** @returns {GiftDrop|null} */
export function normalizeDrop(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = raw.id ?? raw.dropId ?? raw.drop_id;
  if (id === undefined || id === null) return null;
  const createdAt = timeField(raw, 'createdAt');
  const startsAt = timeField(raw, 'startsAt') ?? createdAt;
  const claimOpensAt = timeField(raw, 'claimOpensAt');
  const claimClosesAt = timeField(raw, 'claimClosesAt');
  const expiresAt = timeField(raw, 'expiresAt');
  const collectedAt = timeField(raw, 'collectedAt');
  const lifetimeMs = finiteOrNull(raw.lifetimeMs ?? raw.lifetime_ms);
  const crossingMs = startsAt !== null && expiresAt !== null ? Math.max(0, expiresAt - startsAt) : lifetimeMs;
  const entered = claimOpensAt ?? startsAt;
  const reactionMs = finiteOrNull(raw.reactionMs ?? raw.reaction_ms)
    ?? (collectedAt !== null && entered !== null ? Math.max(0, collectedAt - entered) : null);
  const src = raw.source && typeof raw.source === 'object' ? raw.source : null;
  return {
    id: String(id),
    status: String(raw.status ?? 'active'),
    environment: raw.environment ?? null,
    createdAt,
    startsAt,
    claimOpensAt,
    claimClosesAt,
    expiresAt,
    collectedAt,
    expiredAt: timeField(raw, 'expiredAt'),
    cancelledAt: timeField(raw, 'cancelledAt'),
    crossingMs,
    winner: normalizeWinner(raw.winner ?? raw.collectedBy),
    reactionMs,
    note: String(raw.note ?? ''),
    followersOnly: Boolean(raw.followersOnly ?? raw.followers_only ?? false),
    prize: prizeOf(raw),
    source: src
      ? {
          kind: String(src.kind ?? 'brand'),
          brandId: src.brandId ?? src.brand_id ?? null,
          name: src.name ?? null,
          logoUrl: src.logoUrl ?? src.logo_url ?? null,
        }
      : null,
  };
}

/**
 * The chest's prize: `prize: { products, freeShipping }` (v2.2), else the admin shape's
 * `reward { type: 'products', payload: { products | productIds } }`; null = no prize.
 */
function prizeOf(raw) {
  const p = raw.prize && typeof raw.prize === 'object' ? raw.prize : null;
  let list = Array.isArray(p?.products) ? p.products : null;
  if (!list && raw.reward?.type === 'products' && Array.isArray(raw.reward?.payload?.products)) list = raw.reward.payload.products;
  const products = (list || []).map(normalizeProduct).filter(Boolean);
  if (!products.length) return null;
  return { products, freeShipping: p?.freeShipping !== false };
}

const dropsOf = (list) => (Array.isArray(list) ? list.map(normalizeDrop).filter(Boolean) : []);

export function sortNewestFirst(drops) {
  return [...drops].sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
}

/**
 * The GET list payload (already unwrapped from the envelope). Tolerates a bare
 * array. nextBefore: the API's cursor, else — when it sent none at all and the
 * page came back full — the last row's createdAt.
 */
export function normalizeList(payload, { limit } = {}) {
  const body = Array.isArray(payload) ? { drops: payload } : (payload ?? {});
  const drops = sortNewestFirst(dropsOf(body.drops ?? body.items ?? body.rows));
  let nextBefore = body.nextBefore ?? body.next_before ?? null;
  if (nextBefore === null && body.nextBefore === undefined && limit && drops.length >= limit) {
    const last = drops[drops.length - 1];
    nextBefore = last?.createdAt != null ? new Date(last.createdAt).toISOString() : null;
  }
  return {
    drops,
    nextBefore,
    brandId: body.brandId ?? null,
    environment: body.environment ?? null,
    // The environment's GLOBAL cap and count (not the brand's) — informational only.
    maxActive: finiteOrNull(body.maxActive),
    activeCount: finiteOrNull(body.activeCount),
    serverNowMs: toMs(body.serverNowMs ?? body.server_now_ms),
  };
}

/** A create / cancel payload: `{ drop, serverNowMs }` or the drop itself. */
export function dropFromPayload(payload) {
  return normalizeDrop(payload?.drop ?? payload);
}

/**
 * The first history page read again, merged into what is shown: the fresh page
 * replaces its own rows, and the older rows loaded with "Load more" stay.
 */
export function mergeFirstPage(shown, fresh) {
  if (!shown.length) return fresh;
  const ids = new Set(fresh.map((d) => d.id));
  const oldest = fresh.length ? fresh[fresh.length - 1].createdAt ?? -Infinity : Infinity;
  const tail = shown.filter((d) => !ids.has(d.id) && (d.createdAt ?? -Infinity) < oldest);
  return [...fresh, ...tail];
}

/**
 * Where an active chest is on its way at `nowMs` (server time).
 *   incoming  before it enters the rim     remainingMs → entering
 *   inside    inside the galaxy, catchable remainingMs → the rim on the far side ("a peremig")
 *   leaving   past the rim, drifting out   remainingMs 0
 * progress: 0 → 1 over the catchable span.
 */
export function dropPhase(drop, nowMs) {
  const start = drop.claimOpensAt ?? drop.startsAt ?? drop.createdAt ?? nowMs;
  const end = drop.claimClosesAt ?? drop.expiresAt
    ?? (drop.crossingMs != null ? (drop.startsAt ?? start) + drop.crossingMs : start);
  if (nowMs < start) return { phase: 'incoming', remainingMs: start - nowMs, progress: 0 };
  if (nowMs >= end) return { phase: 'leaving', remainingMs: 0, progress: 1 };
  const span = Math.max(1, end - start);
  return { phase: 'inside', remainingMs: end - nowMs, progress: (nowMs - start) / span };
}

/** 83000 → "1:23" (rounded up, so 0:00 shows only at the rim). */
export function formatCountdown(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** 1234 → "1,23 s" (hu) / "1.23 s" (en); a minute or more → "1:05". */
export function formatReaction(ms, lang = 'hu') {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '—';
  if (ms >= 60000) return formatCountdown(ms);
  const s = (ms / 1000).toLocaleString(lang === 'en' ? 'en-GB' : 'hu-HU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${s} s`;
}

// ---- Live events (the gift:brand:<brandId> socket room) ----

const EVENT_STATUS = {
  gift_drop_spawned: 'active',
  gift_drop_collected: 'collected',
  gift_drop_won: 'collected',
  gift_drop_expired: 'expired',
  gift_drop_cancelled: 'cancelled',
};

/**
 * A socket event → what changed. The page reads the list again after every
 * event anyway (the server stays the source of truth); this only lets it
 * update at once.
 * @returns {{ kind: 'state', drops: GiftDrop[], serverNowMs: number|null }
 *   | { kind: 'upsert', drop: GiftDrop }
 *   | { kind: 'status', id: string, patch: Partial<GiftDrop> }
 *   | null}
 */
export function readGiftEvent(name, data) {
  if (name === 'gift_drops_state') {
    return { kind: 'state', drops: dropsOf(data?.drops), serverNowMs: toMs(data?.serverNowMs) };
  }
  const status = EVENT_STATUS[name];
  if (!status) return null;
  const full = data?.drop ? normalizeDrop(data.drop) : null;
  if (full) {
    const winner = full.winner ?? normalizeWinner(data.winner);
    return { kind: 'upsert', drop: { ...full, status: data.drop.status ? full.status : status, winner } };
  }
  const id = data?.dropId ?? data?.id;
  if (id === undefined || id === null) return null;
  const patch = { status };
  if (typeof data.followersOnly === 'boolean') patch.followersOnly = data.followersOnly;
  if (status === 'collected') {
    patch.winner = normalizeWinner(data.winner);
    patch.collectedAt = timeField(data, 'collectedAt');
  }
  if (status === 'expired') patch.expiredAt = timeField(data, 'expiredAt');
  if (status === 'cancelled') patch.cancelledAt = timeField(data, 'cancelledAt');
  return { kind: 'status', id: String(id), patch };
}

const stripNulls = (patch) =>
  Object.fromEntries(Object.entries(patch).filter(([, v]) => v !== null && v !== undefined));

/** Applies an event to a list of drops (newest first), keeping every other row. */
export function applyGiftEvent(drops, event) {
  if (!event) return drops;
  if (event.kind === 'state') {
    const byId = new Map(drops.map((d) => [d.id, d]));
    for (const d of event.drops) byId.set(d.id, { ...byId.get(d.id), ...d });
    const live = new Set(event.drops.map((d) => d.id));
    // An active drop the snapshot no longer lists has ended; the list re-read tells how.
    return sortNewestFirst([...byId.values()].filter((d) => d.status !== 'active' || live.has(d.id)));
  }
  if (event.kind === 'upsert') {
    return sortNewestFirst([event.drop, ...drops.filter((d) => d.id !== event.drop.id)]);
  }
  if (event.kind === 'status') {
    return drops.map((d) => (d.id === event.id ? { ...d, ...stripNulls(event.patch) } : d));
  }
  return drops;
}

/** The next poll: sooner while a chest is out there. */
export function pollIntervalMs(hasActive) {
  return hasActive ? POLL_ACTIVE_MS : POLL_IDLE_MS;
}

// ---- Errors → one readable line ----

const ERROR_KEYS = {
  gift_drop_max_active: 'gifts.err.galaxyFull',
  gift_drop_not_active: 'gifts.err.notActive',
  gift_drop_already_collected: 'gifts.err.notActive',
  gift_drop_expired: 'gifts.err.notActive',
  gift_drop_cancelled: 'gifts.err.notActive',
  gift_drop_not_found: 'gifts.err.notFound',
  gift_drop_invalid: 'gifts.err.invalid',
  gift_drop_brand_inactive: 'gifts.err.brandInactive',
  gift_drop_not_eligible: 'gifts.err.notEligible',
  // Only on an app claim — never on the portal; here for completeness.
  gift_drop_followers_only: 'gifts.err.followersOnly',
  gift_drop_brand_forbidden: 'gifts.err.forbidden',
  // The permission middleware's own codes (in `error`).
  FORBIDDEN: 'gifts.err.forbidden',
  INSUFFICIENT_PERMISSIONS: 'gifts.err.forbidden',
  BRAND_NOT_FOUND: 'gifts.err.brandNotFound',
  validation_error: 'gifts.err.invalid',
  gift_drops_disabled: 'gifts.err.disabled',
  gift_drop_disabled: 'gifts.err.disabled',
  forbidden: 'gifts.err.forbidden',
  insufficient_permissions: 'gifts.err.forbidden',
  unauthorized: 'gifts.err.auth',
  invalid_token: 'gifts.err.auth',
  missing_token: 'gifts.err.auth',
  rate_limited: 'gifts.err.rateLimited',
  too_many_requests: 'gifts.err.rateLimited',
};

const CODE_RE = /^[A-Za-z][A-Za-z0-9_]*$/;

/** gift_drop_invalid { field: 'productIds', reason } reasons with their own sentence. */
export const PRODUCT_REASONS = ['product_not_found', 'product_inactive', 'product_out_of_stock', 'duplicate_product', 'too_many_products'];

/**
 * The error code of a thrown api() error (config/api.js), if it carries one:
 * the permission middleware's `error` (its `message` is a sentence), else the
 * gift envelope's `message`.
 */
export function errorCodeOf(err) {
  const d = err?.data;
  for (const c of [d?.error, d?.errorCode, d?.message]) {
    if (typeof c === 'string' && CODE_RE.test(c)) return c;
  }
  return null;
}

/** The i18n key (+ vars) for a thrown error. */
export function errorMessageKey(err) {
  if (!err) return null;
  // fetch() rejects with a TypeError when the network is down / CORS fails.
  if (err.name === 'TypeError' && err.status == null) return { key: 'gifts.err.network' };
  const code = errorCodeOf(err);
  const d = err?.data?.details && typeof err.data.details === 'object' ? err.data.details : {};
  if (code === 'gift_drop_invalid' && d.field === 'productIds' && PRODUCT_REASONS.includes(d.reason)) {
    return { key: `gifts.err.product.${d.reason}` };
  }
  if (code === 'gift_drop_invalid' && d.field === 'freeShippingAccepted') return { key: 'gifts.err.freeShipping' };
  if (code === 'gift_drop_not_active' && STATUSES.includes(d.status)) {
    return { key: 'gifts.err.notActiveStatus', vars: { status: d.status } };
  }
  if (code && ERROR_KEYS[code]) return { key: ERROR_KEYS[code] };
  if (err.status === 429) return { key: 'gifts.err.rateLimited' };
  if (err.status === 401) return { key: 'gifts.err.auth' };
  if (err.status === 403) return { key: 'gifts.err.forbidden' };
  // A 404 that names no gift code: the route itself is missing — the API is not deployed here yet.
  if (err.status === 404) return { key: 'gifts.err.unavailable' };
  if (code) return { key: 'gifts.err.genericCode', vars: { code } };
  return { key: 'gifts.err.generic' };
}

/** A thrown error → one readable, translated line. */
export function errorText(err, t) {
  const m = errorMessageKey(err);
  if (!m) return '';
  if (m.key === 'gifts.err.notActiveStatus') {
    return t(m.key, { status: t(`gifts.status.${m.vars.status}`) });
  }
  return t(m.key, m.vars);
}
